/**
 * لایهٔ دادهٔ مشترک بین صفحهٔ اصلی و داشبورد مدیریت
 *
 * داده‌ها روی سرور کمکی (پورت 5178) ذخیره می‌شوند تا همهٔ مرورگرها یک تصویر
 * مشترک ببینند. درحالت توسعه، درخواست‌های /api با پراکسی Vite به آن سرور
 * می‌روند؛ اگر سرور در دسترس نباشد، داشبورد به حافظهٔ محلی مرورگر برمی‌گردد.
 */

export interface VisitRecord {
  /** شناسهٔ پایدار دستگاه (fingerprint) */
  clientId: string
  /** آخرین زمان فعالیت به‌صورت epoch ms */
  lastSeen: number
  /** اولین باری که این دستگاه دیده شده */
  firstSeen: number
  /** تعداد کل بازدیدهای (نشست‌های) این دستگاه */
  visits: number
  /** مشخصات فنی دستگاه */
  device: DeviceInfo
}

export interface DeviceInfo {
  /** مثل «Windows · Chrome ۱۲۰» — برای نمایش در جدول */
  label: string
  os: string
  browser: string
  deviceType: 'موبایل' | 'تبلت' | 'دسکتاپ'
  screen: string
  language: string
}

export interface ConsultationEntry {
  id: string
  name: string
  phone: string
  message: string
  /** epoch ms */
  createdAt: number
  device: DeviceInfo
  clientId: string
}

export interface StatsSnapshot {
  onlineNow: number
  uniqueVisitors: number
  totalVisits: number
  visits: VisitRecord[]
  submissions: ConsultationEntry[]
  /** سرور در دسترس بود یا داده از حافظهٔ محلی مرورگر خوانده شد */
  source: 'server' | 'local'
}

/** مسیر نسبی — با پراکسی Vite به سرور ۵۱۷۸ می‌رسد */
export const ANALYTICS_API = ''

const LOCAL_VISITS_KEY = 'elona_local_visits'
const LOCAL_SUBMISSIONS_KEY = 'elona_local_submissions'

/** پارس سریع User-Agent — بدون وابستگی خارجی */
export function parseUserAgent(ua: string): DeviceInfo {
  const isTablet = /iPad|Tablet|PlayBook|Silk/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))
  const isMobile = /Mobi|iPhone|iPod|Android.*Mobile|Windows Phone/i.test(ua)
  const deviceType: DeviceInfo['deviceType'] = isTablet ? 'تبلت' : isMobile ? 'موبایل' : 'دسکتاپ'

  let os = 'ناشناس'
  if (/Windows NT 10/i.test(ua)) os = 'ویندوز ۱۰/۱۱'
  else if (/Windows/i.test(ua)) os = 'ویندوز'
  else if (/Android/i.test(ua)) os = 'اندروید'
  else if (/iPhone|iPad|iPod/i.test(ua)) os = 'iOS'
  else if (/Mac OS X/i.test(ua)) os = 'مک'
  else if (/Linux/i.test(ua)) os = 'لینوکس'

  let browser = 'ناشناس'
  if (/Edg\//i.test(ua)) browser = 'Edge'
  else if (/OPR\//i.test(ua)) browser = 'Opera'
  else if (/SamsungBrowser/i.test(ua)) browser = 'Samsung Internet'
  else if (/Firefox\//i.test(ua)) browser = 'Firefox'
  else if (/Chrome\//i.test(ua)) browser = 'Chrome'
  else if (/Safari\//i.test(ua)) browser = 'Safari'

  // شمارهٔ نسخهٔ مرورگر
  const match = ua.match(/(?:Edg|OPR|SamsungBrowser|Firefox|Chrome|Version)\/([\d.]+)/i)
  const version = match ? ` ${match[1].split('.')[0]}` : ''

  return {
    os,
    browser: browser + version,
    deviceType,
    label: `${os} · ${browser}${version}`,
    screen: `${window.screen?.width ?? 0}×${window.screen?.height ?? 0}`,
    language: navigator.language ?? '',
  }
}

export function toDeviceInfo(): DeviceInfo {
  return parseUserAgent(navigator.userAgent)
}

// ---------- دسترسی به سرور ----------

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T | null> {
  try {
    const res = await fetch(`${ANALYTICS_API}${path}`, {
      headers: { 'Content-Type': 'application/json' },
      ...init,
    })
    if (!res.ok) return null
    return (await res.json()) as T
  } catch {
    return null
  }
}

/** ثبت/به‌روزرسانی یک دستگاه روی سرور. پاسخ، آمار کامل است. */
export function sendHeartbeat(payload: {
  clientId: string
  device: DeviceInfo
  newSession: boolean
}): Promise<StatsSnapshot | null> {
  return jsonFetch<StatsSnapshot>('/api/heartbeat', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export function fetchStats(): Promise<StatsSnapshot | null> {
  return jsonFetch<StatsSnapshot>('/api/stats')
}

export function submitConsultation(entry: {
  name: string
  phone: string
  message: string
  clientId: string
  device: DeviceInfo
}): Promise<{ ok: boolean } | null> {
  return jsonFetch<{ ok: boolean }>('/api/submissions', {
    method: 'POST',
    body: JSON.stringify(entry),
  })
}

// ---------- جایگزین محلی (وقتی سرور نیست) ----------

function readLocal<T>(key: string): T[] {
  try {
    return JSON.parse(localStorage.getItem(key) ?? '[]') as T[]
  } catch {
    return []
  }
}

function writeLocal<T>(key: string, value: T[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // حافظه در دسترس نیست
  }
}

export function localHeartbeat(clientId: string, device: DeviceInfo, newSession: boolean): StatsSnapshot {
  const visits = readLocal<VisitRecord>(LOCAL_VISITS_KEY)
  const now = Date.now()
  const existing = visits.find((v) => v.clientId === clientId)
  if (existing) {
    existing.lastSeen = now
    if (newSession) existing.visits += 1
  } else {
    visits.push({
      clientId,
      firstSeen: now,
      lastSeen: now,
      visits: 1,
      device,
    })
  }
  writeLocal(LOCAL_VISITS_KEY, visits)

  return computeStats(visits, readLocal<ConsultationEntry>(LOCAL_SUBMISSIONS_KEY), 'local')
}

export function localSubmit(entry: ConsultationEntry): void {
  const submissions = readLocal<ConsultationEntry>(LOCAL_SUBMISSIONS_KEY)
  submissions.push(entry)
  writeLocal(LOCAL_SUBMISSIONS_KEY, submissions)
}

export function localStats(): StatsSnapshot {
  return computeStats(
    readLocal<VisitRecord>(LOCAL_VISITS_KEY),
    readLocal<ConsultationEntry>(LOCAL_SUBMISSIONS_KEY),
    'local',
  )
}

export const ONLINE_WINDOW_MS = 45_000

function computeStats(
  visits: VisitRecord[],
  submissions: ConsultationEntry[],
  source: StatsSnapshot['source'],
): StatsSnapshot {
  const now = Date.now()
  const sorted = [...visits].sort((a, b) => b.lastSeen - a.lastSeen)
  return {
    onlineNow: sorted.filter((v) => now - v.lastSeen < ONLINE_WINDOW_MS).length,
    uniqueVisitors: sorted.length,
    totalVisits: sorted.reduce((sum, v) => sum + v.visits, 0),
    visits: sorted,
    submissions: [...submissions].sort((a, b) => b.createdAt - a.createdAt),
    source,
  }
}

// ---------- قالب‌بندی تاریخ/زمان فارسی ----------

export function formatFaDateTime(ms: number): string {
  return new Intl.DateTimeFormat('fa-IR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(ms))
}

export function formatFaRelative(ms: number): string {
  const diff = Date.now() - ms
  const minutes = Math.floor(diff / 60_000)
  if (minutes < 1) return 'همین حالا'
  if (minutes < 60) return `${minutes} دقیقه پیش`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} ساعت پیش`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days} روز پیش`
  return formatFaDateTime(ms)
}
